import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from '../utils/toastBus';
import SystemInfo from '../components/system/SystemInfo';
import MemoryInfo from '../components/system/MemoryInfo';
import NodeInfo from '../components/system/NodeInfo';
import ServicesStatus from '../components/system/ServicesStatus';
import Recommendations from '../components/system/Recommendations';

function formatBytes(bytes, decimals = 2) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return '0 Bytes';
  const k = 1024;
  const dm = Number.isFinite(decimals) && decimals > 0 ? decimals : 0;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  const value = n / Math.pow(k, i);
  return `${parseFloat(value.toFixed(dm))} ${sizes[i] || 'Bytes'}`;
}

function formatUptime(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds || 0)));
  const days = Math.floor(s / (3600 * 24));
  const hours = Math.floor((s % (3600 * 24)) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = Math.floor(s % 60);
  return `${days}d ${hours}h ${minutes}m ${secs}s`;
}

function extractAnalysisResponse(res) {
  if (!res || typeof res !== 'object') return { error: 'invalid_response', analysis: null };
  if (res.status === 404) {
    return {
      error: 'System Analysis endpoint not available on this deployment (404). Deploy the latest backend or point REACT_APP_API_BASE to the correct backend.',
      analysis: null
    };
  }
  if (res.status === 401 || res.error === 'unauthorized') {
    return { error: 'Unauthorized. Please sign in again.', analysis: null };
  }
  if (res.status === 403 || res.error === 'forbidden') {
    return { error: 'Forbidden. You need admin access to view System Analysis.', analysis: null };
  }
  if (res.data && typeof res.data === 'object') return { error: null, analysis: res.data };
  if (res.success === true && res.data && typeof res.data === 'object') return { error: null, analysis: res.data };
  return { error: res.error || 'Failed to load system analysis', analysis: null };
}

async function copyText(text) {
  const t = String(text || '');
  if (!t) return false;

  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(t);
      return true;
    }
  } catch {
    // ignore and fall back
  }

  try {
    const el = document.createElement('textarea');
    el.value = t;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.top = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

export default function SystemAnalysis() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const lastUpdatedLabel = useMemo(() => {
    const ts = analysis?.timestamp;
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return String(ts);
    }
  }, [analysis?.timestamp]);

  async function fetchAnalysis(showLoading = true) {
    try {
      setError('');
      if (showLoading) setLoading(true);
      else setRefreshing(true);

      const res = await api.getSystemAnalysis();
      const extracted = extractAnalysisResponse(res);
      if (extracted.error) throw new Error(extracted.error);

      setAnalysis(extracted.analysis);
    } catch (e) {
      setError(e?.message || 'Failed to load system analysis');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleRunNewAnalysis() {
    try {
      setError('');
      setRefreshing(true);

      const res = await api.runSystemAnalysis();
      const extracted = extractAnalysisResponse(res);
      if (extracted.error) throw new Error(extracted.error);

      setAnalysis(extracted.analysis);
      toast({ type: 'success', message: 'System analysis completed successfully' });
    } catch (e) {
      setError(e?.message || 'Failed to run system analysis');
    } finally {
      setRefreshing(false);
    }
  }

  async function handleCopyJson() {
    if (!analysis) return;
    const ok = await copyText(JSON.stringify(analysis, null, 2));
    if (ok) toast({ type: 'success', message: 'Analysis copied to clipboard' });
    else toast({ type: 'error', message: 'Failed to copy to clipboard' });
  }

  useEffect(() => {
    fetchAnalysis(true).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: '#111827', borderRadius: 12, padding: 24, border: '1px solid #374151' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ color: '#e5e7eb', marginTop: 0, marginBottom: 0 }}>System Analysis</h2>
            <Link to="/admin" style={{ color: '#ff7a45', textDecoration: 'none' }}>Back to Admin</Link>
          </div>
          <div style={{ display: 'flex', gap: 8, flexDirection: 'column', marginTop: 16 }}>
            <div style={{ height: 20, background: '#1f2937', borderRadius: 4, width: '60%' }} />
            <div style={{ height: 20, background: '#1f2937', borderRadius: 4, width: '80%' }} />
            <div style={{ height: 20, background: '#1f2937', borderRadius: 4, width: '70%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 12, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ color: '#ef4444', marginTop: 0, marginBottom: 0 }}>Error</h2>
            <Link to="/admin" style={{ color: '#ff7a45', textDecoration: 'none' }}>Back to Admin</Link>
          </div>
          <p style={{ color: '#fca5a5', marginBottom: 16 }}>{error}</p>
          <button onClick={() => fetchAnalysis(true)} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: '#111827', borderRadius: 12, padding: 24, border: '1px solid #374151' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ color: '#e5e7eb', marginTop: 0, marginBottom: 0 }}>System Analysis</h2>
            <Link to="/admin" style={{ color: '#ff7a45', textDecoration: 'none' }}>Back to Admin</Link>
          </div>
          <p style={{ color: '#9ca3af', marginTop: 16 }}>No analysis data available.</p>
          <button onClick={() => fetchAnalysis(true)} style={{ background: '#374151', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ color: '#ff7a45', margin: 0 }}>System Analysis</h2>
          <p style={{ color: '#9ca3af', fontSize: 14, margin: '4px 0 0 0' }}>Last updated: {lastUpdatedLabel}</p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Link to="/admin" style={{ color: '#ff7a45', textDecoration: 'none', padding: '8px 12px', borderRadius: 6, border: '1px solid #374151', background: '#0f172a' }}>
            Back to Admin
          </Link>
          <button
            onClick={() => fetchAnalysis(false)}
            disabled={refreshing}
            style={{
              padding: '8px 16px',
              background: refreshing ? '#4b5563' : '#374151',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: refreshing ? 'not-allowed' : 'pointer'
            }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={handleRunNewAnalysis}
            disabled={refreshing}
            style={{
              padding: '8px 16px',
              background: refreshing ? '#2563eb' : '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: refreshing ? 'not-allowed' : 'pointer'
            }}
          >
            {refreshing ? 'Running Analysis...' : 'Run New Analysis'}
          </button>
          <button
            onClick={handleCopyJson}
            style={{ padding: '8px 16px', background: '#ff5a1f', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            Copy to Clipboard
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        <SystemInfo system={analysis.system} formatUptime={formatUptime} />
        <MemoryInfo memory={analysis.memory} formatBytes={formatBytes} />
        <NodeInfo node={analysis.node} processInfo={analysis.process} formatUptime={formatUptime} />
      </div>

      <ServicesStatus services={analysis.services} />

      {Array.isArray(analysis.recommendations) && analysis.recommendations.length > 0 ? (
        <Recommendations recommendations={analysis.recommendations} />
      ) : null}

      <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16, marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ color: '#e5e7eb', margin: 0, fontSize: 18 }}>Raw Data</h3>
          <button
            onClick={handleCopyJson}
            style={{ padding: '6px 12px', background: '#374151', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
          >
            Copy JSON
          </button>
        </div>
        <div style={{ background: '#0f172a', borderRadius: 6, padding: 16, maxHeight: 400, overflow: 'auto', fontFamily: 'monospace', fontSize: 13, color: '#e5e7eb', whiteSpace: 'pre' }}>
          {JSON.stringify(analysis, null, 2)}
        </div>
      </div>
    </div>
  );
}
